/**
 * Video Translator Agent - 主应用组件
 * 
 * 功能流程：
 * 1. 用户授权 (BYOP / Dev Key)
 * 2. 上传短视频
 * 3. FFmpeg 提取音频
 * 4. Pollinations Whisper API 语音转录
 * 5. Pollinations Chat API 字幕翻译
 * 6. 字幕编辑与导出
 * 7. Pollinations TTS API 语音合成
 */

import React, { useState, useCallback } from 'react'
import AuthPanel from './components/AuthPanel'
import VideoUploader from './components/VideoUploader'
import SubtitleEditor from './components/SubtitleEditor'
import { useTranscription, useTranslation, useBatchTTS } from './hooks/usePollinations'
import { useFFmpeg } from './hooks/useFFmpeg'
import { transcriptionToSrt, downloadTextFile } from './utils/subtitleUtils'

// 生产环境：通过 Vite 环境变量预配置 Key，页面上不显示输入框
const CONFIGURED_APP_KEY = import.meta.env.VITE_POLLINATIONS_APP_KEY || ''
const CONFIGURED_DEV_KEY = import.meta.env.VITE_POLLINATIONS_DEV_KEY || ''

export default function App() {
  // 应用配置状态（未配置环境变量时，允许用户本地输入）
  const [appKey, setAppKey] = useState(() => CONFIGURED_APP_KEY || localStorage.getItem('pollinations_app_key') || '')
  const devKey = CONFIGURED_DEV_KEY || localStorage.getItem('pollinations_dev_key') || ''

  // 视频与处理状态
  const [videoFile, setVideoFile] = useState(null)
  const [segments, setSegments] = useState(null)
  const [translatedTexts, setTranslatedTexts] = useState(null)
  const [globalProgress, setGlobalProgress] = useState('')

  // 配置状态
  const [sourceLanguage, setSourceLanguage] = useState('auto')
  const [targetLanguage, setTargetLanguage] = useState('zh')
  const [ttsVoice, setTtsVoice] = useState('nova')

  // Hooks
  const { extractAudio, loading: ffmpegLoading, progress: ffmpegProgress } = useFFmpeg()
  const { transcribe, loading: transcribing, result: transcriptionResult } = useTranscription(devKey)
  const { translate, loading: translating, result: translationResult } = useTranslation(devKey)
  const { synthesizeBatch, loading: ttsLoading, audioUrls } = useBatchTTS(devKey)

  // 保存 App Key
  const handleAppKeyChange = useCallback((key) => {
    setAppKey(key)
    localStorage.setItem('pollinations_app_key', key)
  }, [])

  // 步骤 1: 上传视频后提取音频并转录
  const handleExtractAndTranscribe = useCallback(async () => {
    if (!videoFile) return

    setGlobalProgress('正在提取音频...')
    const audioBlob = await extractAudio(videoFile)
    if (!audioBlob) {
      setGlobalProgress('')
      return
    }

    setGlobalProgress('正在语音识别...')
    const result = await transcribe(audioBlob, sourceLanguage)
    if (result?.segments) {
      setSegments(result.segments)
    }
    setGlobalProgress('')
  }, [videoFile, extractAudio, transcribe, sourceLanguage])

  // 步骤 2: 翻译字幕
  const handleTranslate = useCallback(async () => {
    if (!segments) return
    const texts = segments.map((s) => s.text)
    const translated = await translate(texts, targetLanguage, sourceLanguage)
    if (translated) {
      setTranslatedTexts(translated)
    }
  }, [segments, translate, targetLanguage, sourceLanguage])

  // 更新翻译文本
  const handleUpdateTranslation = useCallback((updated) => {
    setTranslatedTexts(updated)
  }, [])

  // 步骤 3: 批量语音合成
  const handleSynthesizeAll = useCallback(async () => {
    if (!segments || !translatedTexts) return
    const segs = segments.map((s, i) => ({ ...s, text: translatedTexts[i] || s.text }))
    await synthesizeBatch(segs, ttsVoice, targetLanguage)
  }, [segments, translatedTexts, synthesizeBatch, ttsVoice, targetLanguage])

  // 导出翻译后的 SRT
  const handleExportSrt = useCallback(() => {
    if (!segments || !translatedTexts) return
    const segs = segments.map((s, i) => ({ ...s, text: translatedTexts[i] || s.text }))
    const srt = transcriptionToSrt(segs)
    downloadTextFile(srt, `translated_${targetLanguage}.srt`)
  }, [segments, translatedTexts, targetLanguage])

  // 是否正在处理
  const isProcessing = ffmpegLoading || transcribing || translating || ttsLoading
  const currentProgress = globalProgress || ffmpegProgress || ''

  return (
    <div style={styles.app}>
      {/* Header */}
      <header style={styles.header}>
        <div style={styles.logoRow}>
          <img
            src="https://pollinations.ai/logo.svg"
            alt="Pollinations"
            style={styles.logo}
          />
          <h1 style={styles.heading}>Video Translator Agent</h1>
        </div>
        <p style={styles.subheading}>
          AI 短视频语音与字幕自动翻译 · Powered by{' '}
          <a href="https://pollinations.ai" target="_blank" rel="noopener noreferrer" style={styles.link}>
            Pollinations
          </a>
        </p>
      </header>

      <main style={styles.main}>
        {/* 全局进度条 */}
        {currentProgress && (
          <div style={styles.progressBar}>
            <div style={styles.progressInner}>{currentProgress}</div>
          </div>
        )}

        {/* 认证面板 */}
        <AuthPanel
          appKey={appKey}
          onKeyChange={handleAppKeyChange}
          isEmbedded={!!CONFIGURED_APP_KEY}
        />

        {/* 视频上传 */}
        <VideoUploader
          onVideoSelect={setVideoFile}
          videoFile={videoFile}
          disabled={isProcessing}
        />

        {/* 提取音频 & 转录按钮 */}
        {videoFile && !segments && (
          <div style={styles.stepAction}>
            <button
              onClick={handleExtractAndTranscribe}
              disabled={isProcessing}
              style={{
                ...styles.bigButton,
                ...(isProcessing ? styles.bigButtonDisabled : {}),
              }}
            >
              {transcribing ? '正在转录...' : '🔍 提取音频并语音识别'}
            </button>
          </div>
        )}

        {/* 字幕编辑与翻译 */}
        <SubtitleEditor
          segments={segments}
          translatedTexts={translatedTexts}
          onUpdateTranslation={handleUpdateTranslation}
          targetLanguage={targetLanguage}
          onTargetLanguageChange={setTargetLanguage}
          sourceLanguage={sourceLanguage}
          onSourceLanguageChange={setSourceLanguage}
          ttsVoice={ttsVoice}
          onTtsVoiceChange={setTtsVoice}
          onTranslate={handleTranslate}
          translating={translating}
          onExportSrt={handleExportSrt}
          onSynthesizeAll={handleSynthesizeAll}
          synthesizing={ttsLoading}
        />

        {/* TTS 结果播放 */}
        {audioUrls.length > 0 && (
          <div style={styles.ttsResults}>
            <h3 style={styles.title}>🔊 合成语音片段</h3>
            <div style={styles.audioList}>
              {audioUrls.map((url, i) =>
                url ? (
                  <div key={i} style={styles.audioItem}>
                    <span style={styles.audioLabel}>片段 #{i + 1}</span>
                    <audio src={url} controls style={styles.audioPlayer} />
                  </div>
                ) : null
              )}
            </div>
          </div>
        )}
      </main>

      {/* Footer */}
      <footer style={styles.footer}>
        <div style={styles.footerContent}>
          <span>Built with ❤️ using </span>
          <a href="https://pollinations.ai" target="_blank" rel="noopener noreferrer" style={styles.link}>
            Pollinations API
          </a>
          <span> · </span>
          <a
            href="https://github.com/pollinations/pollinations"
            target="_blank"
            rel="noopener noreferrer"
            style={styles.link}
          >
            GitHub
          </a>
        </div>
      </footer>
    </div>
  )
}

const styles = {
  app: {
    minHeight: '100vh',
    background: 'linear-gradient(180deg, #0a0a0f 0%, #0f172a 100%)',
    display: 'flex',
    flexDirection: 'column',
  },
  header: {
    textAlign: 'center',
    padding: '32px 20px 20px',
    borderBottom: '1px solid rgba(138, 43, 226, 0.15)',
  },
  logoRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '12px',
    marginBottom: '8px',
  },
  logo: {
    width: '36px',
    height: '36px',
  },
  heading: {
    margin: 0,
    fontSize: '28px',
    fontWeight: 700,
    background: 'linear-gradient(135deg, #c084fc, #8a2be2)',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
  },
  subheading: {
    margin: '4px 0 0',
    color: '#94a3b8',
    fontSize: '14px',
  },
  link: {
    color: '#c084fc',
    textDecoration: 'none',
  },
  main: {
    flex: 1,
    maxWidth: '960px',
    width: '100%',
    margin: '0 auto',
    padding: '24px 20px',
    boxSizing: 'border-box',
  },
  progressBar: {
    marginBottom: '16px',
    padding: '12px 16px',
    background: 'linear-gradient(135deg, #8a2be2, #6b21a8)',
    borderRadius: '10px',
    color: '#fff',
    fontSize: '14px',
    fontWeight: 500,
    textAlign: 'center',
  },
  progressInner: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
  },
  stepAction: {
    display: 'flex',
    justifyContent: 'center',
    marginBottom: '20px',
  },
  bigButton: {
    padding: '14px 32px',
    borderRadius: '12px',
    border: 'none',
    background: 'linear-gradient(135deg, #8a2be2, #6b21a8)',
    color: '#fff',
    fontSize: '16px',
    fontWeight: 600,
    cursor: 'pointer',
    boxShadow: '0 4px 20px rgba(138, 43, 226, 0.3)',
  },
  bigButtonDisabled: {
    opacity: 0.6,
    cursor: 'not-allowed',
  },
  ttsResults: {
    background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)',
    borderRadius: '16px',
    padding: '24px',
    border: '1px solid rgba(138, 43, 226, 0.3)',
    marginBottom: '20px',
  },
  title: {
    margin: '0 0 16px 0',
    color: '#e2e8f0',
    fontSize: '18px',
  },
  audioList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
  },
  audioItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '10px 14px',
    background: 'rgba(15, 23, 42, 0.5)',
    borderRadius: '10px',
  },
  audioLabel: {
    color: '#8a2be2',
    fontSize: '13px',
    fontWeight: 600,
    minWidth: '70px',
  },
  audioPlayer: {
    flex: 1,
    height: '36px',
  },
  footer: {
    padding: '20px',
    textAlign: 'center',
    borderTop: '1px solid rgba(138, 43, 226, 0.1)',
  },
  footerContent: {
    color: '#64748b',
    fontSize: '13px',
  },
}